/**
 * BlackoutButton - kill the rig.
 *
 * ## No target, and that is the feature
 *
 * Every other lighting control carries a `DmxTarget`. This one does not. A
 * kill switch that can be aimed is a kill switch that can be aimed wrong in
 * the one moment it matters, and `POST /dmx/blackout` is rig-wide on
 * OPBridge's side anyway. There is nothing to configure, so there is nothing
 * to configure wrong - which also means this is the one lighting control that
 * cannot have a dead state caused by the rig.
 *
 * ## It fans out to EVERY bridge, unlike every other DMX send
 *
 * `sendDmxState` and `sendDmxEffect` deliberately hit one bridge, because
 * OPBridge routes a drive command to whichever node owns the fixture. This
 * does not: it goes to every reachable bridge, for the same reason
 * `/midi/panic` does. Re-firing is half of what a panic is for, and a
 * rig-wide kill that reached one node while another kept a strobe running is
 * the worst available outcome. See `sendDmxBlackout`.
 *
 * ## Guarded by DEFAULT, uniquely
 *
 * Every other item's show-mode guard (SD-5 / DEC-072) defaults off. This one
 * defaults ON, set at spawn in `addBlackout`. A rig-wide kill is exactly the
 * control worth one extra tap, and the operator reaching for it in a hurry is
 * also the one most likely to brush past it. It stays editable - a default,
 * not a lock - because an operator who wants a one-tap panic has a real
 * reason and this is their instrument.
 */

import { createSignal } from 'solid-js';
import { editOwnsGesture, selectedItemId, type BlackoutItem, type LayoutColor } from '../layout/store.ts';
import { anyBridgeConnectedReactive, bridges } from '../opbridge/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { sendDmxBlackout } from '../opbridge/dispatch.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface BlackoutButtonProps {
  item: BlackoutItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function BlackoutButton(props: BlackoutButtonProps) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [firing, setFiring] = createSignal(false);

  // Blackout goes to EVERY bridge, so any one answering makes it worth
  // pressing: DMX lives on STREAMPC, and the default being down used to
  // disable the rig's light kill (2026-11-03 audit A-9).
  const live = () => anyBridgeConnectedReactive(bridges().map((b) => b.id));

  async function fire(): Promise<void> {
    setFiring(true);
    try {
      const results = await sendDmxBlackout();
      const ok = results.filter((r) => r.kind === 'ok').length;
      const total = results.length;
      if (ok === total && total > 0) {
        // Says how many bridges answered even on success, because PANIC has
        // already been measured reaching only ONE bridge on this rig (CP-121
        // live-risk audit). A bare "Blackout" would hide exactly the failure
        // this rig has actually had.
        pushToast('ok', total === 1 ? 'Blackout' : `Blackout - ${total} bridges`, 1500);
      } else {
        const why = results.find((r) => r.kind !== 'ok');
        pushToast('error', `Blackout reached ${ok}/${total}${why?.detail ? ` - ${why.detail}` : ''}`, 4000);
      }
    } finally {
      setFiring(false);
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    if (!live()) return;
    e.stopPropagation();
    if (!guard.gate('fire')) return;
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel;
  const faceText = () => props.item.name ?? 'BLACKOUT';
  const subText = () => {
    if (!live()) return 'no bridge';
    if (firing()) return 'killing...';
    if (guard.armedKey() === 'fire') return 'tap again to kill';
    return props.item.guard ? 'guarded' : 'all lights off';
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-on="false"
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      onPointerDown={onPointerDown}
      disabled={!live()}
    >
      <PadDecor item={props.item} />
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && <span class="midi-button-label">{subText()}</span>}
    </button>
  );
}
